/**
 * The English dictionary, and the source of truth for what keys exist.
 *
 * `as const` is what makes that true: `TranslationKey` is derived from these keys,
 * and every other language is typed as `Record<TranslationKey, string>`, so a
 * missing or misspelled translation fails `tsc` rather than falling back silently
 * at runtime in front of a user.
 *
 * Placeholders are `{name}` and are substituted by `translate()`.
 */
export const en = {
  // ─── Shared ────────────────────────────────────────────────────────────────
  'common.appName': 'Social Content Studio',
  'common.appTagline': 'Plan, create and publish across every channel',
  'common.brand': 'Social Studio',
  'common.back': 'Back',
  'common.dashboard': 'Dashboard',
  'common.previous': 'Previous',
  'common.continue': 'Continue',
  'common.loading': 'Loading…',
  'common.processing': 'Processing…',
  'common.cancel': 'Cancel',
  'common.confirm': 'Confirm',
  'common.edit': 'Edit',
  'common.done': 'Done',
  'common.view': 'View',
  'common.prev': 'Prev',
  'common.next': 'Next',
  'common.signIn': 'Sign In',
  'common.signOut': 'Sign out',
  'common.expired': 'Expired',
  'common.emptyValue': '—',

  // ─── Language toggle ───────────────────────────────────────────────────────
  'language.switchTo': 'Switch to {language}',

  // ─── Payment / subscription statuses ───────────────────────────────────────
  'status.pending': 'Pending',
  'status.completed': 'Completed',
  'status.failed': 'Failed',
  'status.cancelled': 'Cancelled',
  'status.active': 'Active',
  'status.expired': 'Expired',

  // ─── Platforms ─────────────────────────────────────────────────────────────
  'platform.facebook': 'Facebook',
  'platform.instagram': 'Instagram',
  'platform.linkedin': 'LinkedIn',
  'platform.tiktok': 'TikTok',
  'platform.youtube': 'YouTube',

  // ─── Industry verticals ────────────────────────────────────────────────────
  'vertical.restaurant': 'Restaurant & café',
  'vertical.dental': 'Dental clinic',
  'vertical.real_estate': 'Real estate',
  'vertical.cleaning': 'Cleaning company',
  'vertical.agency': 'Agency',
  'vertical.other': 'Other',

  // ─── Post lifecycle ────────────────────────────────────────────────────────
  'postStatus.draft': 'Draft',
  'postStatus.pending_review': 'Awaiting review',
  'postStatus.approved': 'Approved',
  'postStatus.scheduled': 'Scheduled',
  'postStatus.publishing': 'Publishing…',
  'postStatus.published': 'Published',
  'postStatus.failed': 'Failed',
  'postStatus.cancelled': 'Cancelled',

  // ─── Login ─────────────────────────────────────────────────────────────────
  'login.title': 'Social Content Studio',
  'login.subtitle': 'AI content, scheduled and published for you',
  'login.welcome': 'Welcome back',
  'login.description': 'Sign in to manage your content and connected accounts.',
  'login.google': 'Continue with Google',
  'login.terms':
    'By signing in you agree to our Terms of Service. New users get a 14-day free trial.',
  'login.suspendedTitle': 'This account is suspended',
  'login.suspendedBody':
    'Sign-in is blocked while the suspension is in force. Contact support if you believe this is a mistake.',
  'login.failedTitle': 'Sign-in failed',

  // ─── Session expiry ────────────────────────────────────────────────────────
  'session.title': 'Your session has expired',
  'session.description':
    'You have been signed out for security. Log in again to carry on — any unsaved edits will need to be redone.',
  'session.logout': 'Log out',

  // ─── Account suspension ────────────────────────────────────────────────────
  'suspended.title': 'Your account is suspended',
  'suspended.description':
    'An administrator has suspended this account, so it cannot be used until the suspension ends. Signing in again will not restore access.',
  'suspended.descriptionIndefinite':
    'An administrator has suspended this account indefinitely. Signing in again will not restore access — contact support if you believe this is a mistake.',
  'suspended.reasonLabel': 'Reason',
  'suspended.untilLabel': 'Suspended until',
  'suspended.indefinite': 'Indefinitely',
  'suspended.logout': 'Log out',

  // ─── Dashboard navigation ──────────────────────────────────────────────────
  'nav.content': 'Content',
  'nav.setup': 'Setup',
  'nav.analytics': 'Analytics',
  'nav.account': 'Account',
  'nav.overview': 'Overview',
  'nav.calendar': 'Calendar',
  'nav.composer': 'Composer',
  'nav.approvals': 'Approvals',
  'nav.library': 'Media library',
  'nav.brands': 'Brands',
  'nav.accounts': 'Connected accounts',
  'nav.subscription': 'Subscription',
  'nav.payments': 'Payment History',
  'nav.admin': 'Administration',
  'nav.adminUsers': 'Users',
  'nav.adminActivity': 'User Activity',
  'nav.adminStats': 'Statistics',
  'nav.collapse': 'Collapse panel',
  'nav.resize': 'Resize panel — use arrow keys',
  'nav.dragResize': 'Drag to resize',
  'nav.breadcrumb': 'Home / Dashboard',

  // ─── Dashboard: overview ───────────────────────────────────────────────────
  'dashboard.heroTitle': 'Your whole content calendar, written and scheduled 📣',
  'dashboard.heroBody':
    'Describe your business once. Generate posts for every platform, review them, and let them publish themselves.',
  'dashboard.heroCta': 'Set up your brand',
  'dashboard.postsThisMonth': 'Posts published',
  'dashboard.generationsThisMonth': 'AI generations',
  'dashboard.ofLimit': 'of {limit} this month',
  'dashboard.getStarted': 'Get started',
  'dashboard.stepBrandTitle': 'Describe your business',
  'dashboard.stepBrandBody':
    'Industry, services, tone and audience. Every post is written from this.',
  'dashboard.stepConnectTitle': 'Connect your accounts',
  'dashboard.stepConnectBody': 'Link the pages and profiles you want to publish to.',
  'dashboard.stepComposeTitle': 'Generate a post',
  'dashboard.stepComposeBody': 'One idea becomes a tailored version for each platform.',
  'dashboard.stepScheduleTitle': 'Fill your calendar',
  'dashboard.stepScheduleBody': 'Pick times and let scheduled posts publish on their own.',

  // ─── Not-yet-built tabs ────────────────────────────────────────────────────
  'placeholder.calendarTitle': 'Calendar is coming next',
  'placeholder.calendarBody':
    'Scheduling arrives with the publishing pipeline. Connected accounts and content generation come first.',
  'placeholder.composerTitle': 'Composer is coming next',
  'placeholder.composerBody':
    'AI generation lands in the next phase, once brand profiles exist to write from.',
  'placeholder.brandsTitle': 'Brand setup is coming next',
  'placeholder.brandsBody':
    'This is the first thing being built — the business profile every generated post is written from.',
  'placeholder.accountsTitle': 'Account connections are coming soon',
  'placeholder.accountsBody':
    'Connecting Facebook, Instagram, LinkedIn, TikTok and YouTube depends on platform review, which is already under way.',
  'placeholder.libraryTitle': 'Media library is coming soon',
  'placeholder.libraryBody': 'Image and video uploads arrive with the media pipeline.',
  'placeholder.approvalsTitle': 'Approvals are coming soon',
  'placeholder.approvalsBody':
    'Review-before-publish arrives with scheduling. It will be on by default for regulated industries.',
  'placeholder.analyticsTitle': 'Analytics are coming soon',
  'placeholder.analyticsBody':
    'Per-post performance appears once posts are publishing and there is something to measure.',

  // ─── Dashboard: subscription tab ───────────────────────────────────────────
  'subscription.signInPrompt': 'Sign in to manage your subscription.',
  'subscription.loadError': 'Could not load subscription.',
  'subscription.planLabel': '{plan} Plan',
  'subscription.daysLeft': '{days}d left',
  'subscription.daysShort': '{days}d',
  'subscription.days': '{days} days',
  'subscription.postsUsed': 'Posts published',
  'subscription.generationsUsed': 'AI generations',
  'subscription.storageUsed': 'Media storage',
  'subscription.expiryWarning': 'Expires in {days} days. Renew soon.',
  'subscription.expiryWarningOne': 'Expires in 1 day. Renew soon.',
  'subscription.viewUpgrade': 'View / Upgrade Plans',
  'subscription.renewalTitle': 'Your subscription has expired',
  'subscription.renewalBody': 'Subscribe again to carry on publishing.',
  'subscription.renewalAction': 'View plans',
  'subscription.renewalDismiss': 'Remind me later',
  'subscription.cancelAction': 'Cancel Subscription',
  'subscription.cancelling': 'Cancelling…',
  'subscription.cancelFailed': 'Failed to cancel. Please try again.',
  'subscription.confirmTitle': 'Cancel your subscription?',
  'subscription.confirmBody':
    'You keep access until the current period ends. Scheduled posts beyond that date will not publish.',
  'subscription.confirmYes': 'Cancel subscription',
  'subscription.confirmNo': 'Keep it',
  'subscription.limitsTitle': 'Your plan limits',
  'subscription.limitBrands': 'Brands:',
  'subscription.limitAccounts': 'Connected accounts:',
  'subscription.limitHorizon': 'Schedule ahead:',
  'subscription.postsRemaining': 'Posts remaining this month:',

  // ─── Dashboard: payments tab ───────────────────────────────────────────────
  'payments.signInPrompt': 'Sign in to see your payments.',
  'payments.emptyTitle': 'No payments yet',
  'payments.emptyBody': 'Your purchases will show up here once you upgrade.',
  'payments.viewPlans': 'View Plans',

  // ─── Pricing ───────────────────────────────────────────────────────────────
  'pricing.badge': 'Subscription Plans',
  'pricing.title': 'Choose your plan',
  'pricing.subtitle':
    'AI-written content, scheduled and published across every channel you connect.',
  'pricing.perMonth': 'per month',
  'pricing.days14': '14 days',
  'pricing.contactUs': 'contact us',
  'pricing.custom': 'Custom',
  'pricing.badgeFree': '14 Days Free',
  'pricing.badgeBest': 'Best Value',
  'pricing.planFreeTrial': 'Free Trial',
  'pricing.planStarter': 'Starter',
  'pricing.planGrowth': 'Growth',
  'pricing.planAgency': 'Agency',
  'pricing.planEnterprise': 'Enterprise',
  'pricing.planLegacyProTrial': 'Pro Trial',
  'pricing.planLegacyPro': 'Pro',
  'pricing.ctaFreeTrial': 'Start Free Trial',
  'pricing.ctaStarter': 'Get Starter',
  'pricing.ctaGrowth': 'Get Growth',
  'pricing.ctaAgency': 'Get Agency',
  'pricing.ctaEnterprise': 'Contact Sales',
  'pricing.featBrands': '{count} brand',
  'pricing.featBrandsPlural': '{count} brands',
  'pricing.featAccounts': '{count} connected accounts',
  'pricing.featPosts': '{count} posts per month',
  'pricing.featGenerations': '{count} AI generations per month',
  'pricing.featStorage': '{amount} media storage',
  'pricing.featPlatforms': 'Publishes to {platforms}',
  'pricing.featScheduleHorizon': 'Schedule up to {days} days ahead',
  'pricing.featApprovals': 'Approval workflow',
  'pricing.featAnalytics': 'Post analytics',
  'pricing.featWorkspaces': 'Client workspaces',
  'pricing.featSupport': 'Dedicated support',
  'pricing.featCustomVolume': 'Custom volume',
  'pricing.featMultiUser': 'Multi-user access',
  'pricing.featBranding': 'Custom branding',
  'pricing.featInvoice': 'Invoice billing',
  'pricing.payFailed': 'Payment failed. Please try again.',

  // ─── Payment result ────────────────────────────────────────────────────────
  'payment.successTitle': 'Payment Successful!',
  'payment.successBody': 'Your subscription is now active. Start planning your content right away.',
  'payment.plan': 'Plan',
  'payment.postsPerMonth': 'Posts/month',
  'payment.expires': 'Expires',
  'payment.activating': 'Activating subscription…',
  'payment.tranId': 'Transaction ID: {id}',
  'payment.goToDashboard': 'Go to Dashboard',
  'payment.receipt': 'A receipt has been sent to your registered email address.',
  'payment.failTitle': 'Payment Failed',
  'payment.failBody': 'Your payment could not be processed. No charge was made.',
  'payment.tryAgain': 'Try Again',
  'payment.backToDashboard': 'Back to Dashboard',
  'payment.cancelTitle': 'Payment Cancelled',
  'payment.cancelBody': 'You cancelled the payment. Your current plan remains unchanged.',
  'payment.viewPlans': 'View Plans',

  // ─── Admin ─────────────────────────────────────────────────────────────────
  'admin.searchUsers': 'Search by name or email',
  'admin.colUser': 'User',
  'admin.colPlan': 'Plan',
  'admin.colSignedUp': 'Signed up',
  'admin.colLastSeen': 'Last seen',
  'admin.colGenerations': 'Generations',
  'admin.roleBadge': 'Admin',
  'admin.viewActivity': 'View activity',
  'admin.colAccount': 'Account',
  'admin.accountOk': 'Active',
  'admin.bannedBadge': 'Suspended',
  'admin.bannedUntil': 'Until {date}',
  'admin.ban': 'Suspend',
  'admin.unban': 'Lift',
  'admin.banTitle': 'Suspend this account',
  'admin.banDescription':
    '{user} will be signed out and blocked from the API and from signing in again.',
  'admin.banDuration': 'Duration',
  'admin.banDays': '{days} days',
  'admin.banIndefinite': 'Indefinite',
  'admin.banReason': 'Reason',
  'admin.banReasonPlaceholder': 'e.g. Automated abuse of the generation endpoint',
  'admin.banReasonHint': 'Shown to the user on every attempt to use the app.',
  'admin.banConfirm': 'Suspend account',
  'admin.banFailed': 'Could not suspend that account.',
  'admin.unbanFailed': 'Could not lift that suspension.',
  'admin.noUsers': 'No users match that search.',
  'admin.noActivity': 'Nothing logged yet. Activity appears here as people use the product.',
  'admin.filterAll': 'All event types',
  'admin.filterClear': 'Clear filters',
  'admin.filteredToUser': 'Showing activity for one user only.',
  'admin.showing': 'Showing {from}–{to} of {total}',
  'admin.never': 'Never',
  'admin.statTotalUsers': 'Total Users',
  'admin.statNewUsers': 'New in {days} days',
  'admin.statActiveSubs': 'Active Subscriptions',
  'admin.statGenerations': 'Content Generated',
  'admin.statPostsPublished': 'Posts Published',
  'admin.statConnectedAccounts': 'Connected Accounts',
  'admin.statNeedsReauth': 'Need Reconnecting',
  'admin.statRevenue': 'Revenue ({days}d)',
  'admin.perDayTitle': 'Generations per day',
  'admin.postsPerDayTitle': 'Posts published per day',
  'admin.planDistTitle': 'Active plans',
  'admin.platformDistTitle': 'Connected platforms',
  'admin.eventBreakdownTitle': 'Events by type',
  'admin.rangeDays': 'Last {days} days',
  'admin.noStats': 'Not enough data yet.',

  // ─── Admin: activity event labels ──────────────────────────────────────────
  'activity.user_signed_up': 'Signed up',
  'activity.user_signed_in': 'Signed in',
  'activity.content_generated': 'Generated content',
  'activity.generation_blocked': 'Generation blocked',
  'activity.generation_failed': 'Generation failed',
  'activity.brand_created': 'Created a brand',
  'activity.account_connected': 'Connected an account',
  'activity.account_disconnected': 'Disconnected an account',
  'activity.account_reauth_required': 'Account needs reconnecting',
  'activity.post_scheduled': 'Scheduled a post',
  'activity.post_published': 'Published a post',
  'activity.post_failed': 'Post failed to publish',
  'activity.post_approved': 'Approved a post',
  'activity.post_rejected': 'Rejected a post',
  'activity.token_refresh_failed': 'Token refresh failed',
  'activity.payment_initiated': 'Started a payment',
  'activity.payment_completed': 'Payment completed',
  'activity.payment_failed': 'Payment failed',
  'activity.subscription_activated': 'Plan activated',
  'activity.subscription_cancelled': 'Plan cancelled',
  'activity.user_banned': 'Account suspended',
  'activity.user_unbanned': 'Suspension lifted',
  // Retired event, retained so historical rows still render a label.
  'activity.question_generated': 'Generated a paper (legacy)',
} as const;
